import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { t } from "../lib/i18n";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo ha fallado. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t("Introduce tu email."));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-background">
      <div className="max-w-md w-full space-y-7 fade-up">
        <a href="/" className="flex items-center gap-3" data-testid="forgot-logo">
          <div className="w-7 h-7 hairline flex items-center justify-center bg-[#1E3A5F]">
            <span className="text-bone font-serif-display text-sm leading-none">R</span>
          </div>
          <span className="font-serif-display text-xl tracking-tight">{t("Relevo")}</span>
        </a>

        {sent ? (
          <div className="space-y-6">
            <div className="w-12 h-12 hairline flex items-center justify-center bg-forest/10">
              <CheckCircle2 className="w-6 h-6 text-forest" />
            </div>
            <div className="space-y-3">
              <span className="font-mono-label text-gold">{t("Email enviado")}</span>
              <h1 className="font-serif-display text-3xl md:text-4xl leading-tight">{t("Revisa tu bandeja de entrada.")}</h1>
              <p className="text-muted-foreground">
                {t("Si")}{" "}<span className="font-medium text-foreground">{email}</span>{" "}{t("está registrado en Relevo, te hemos enviado un enlace para restablecer tu contraseña. Es válido durante 1 hora.")}
              </p>
              <p className="text-muted-foreground text-sm">
                {t("¿No lo encuentras? Revisa tu carpeta de spam o vuelve a intentarlo en unos minutos.")}
              </p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 font-mono-label text-gold hover:underline"
              data-testid="forgot-back-login"
            >
              <ArrowLeft className="w-3 h-3" />{" "}{t("Volver al inicio de sesión")}
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <span className="font-mono-label text-gold">{t("Recuperar acceso")}</span>
              <h1 className="font-serif-display text-4xl md:text-5xl leading-tight">
                {t("¿Olvidaste tu contraseña?")}
              </h1>
              <p className="text-muted-foreground text-sm">
                {t("Introduce el email con el que te registraste y te enviaremos un enlace para crear una nueva contraseña.")}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div className="space-y-1.5">
                <Label className="font-mono-label text-muted-foreground">{t("Email")}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                    <Mail className="w-4 h-4" />
                  </span>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("tu@email.com")}
                    className="rounded-none pl-10"
                    data-testid="forgot-email-input"
                    autoFocus
                    autoComplete="email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="rounded-none w-full bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12"
                data-testid="forgot-submit-btn"
              >
                {loading ? t("Enviando…") : t("Enviar enlace de recuperación")}
              </Button>
            </form>

            <Link
              to="/login"
              className="inline-flex items-center gap-2 font-mono-label text-muted-foreground hover:text-foreground"
              data-testid="forgot-cancel"
            >
              <ArrowLeft className="w-3 h-3" />{" "}{t("Volver al inicio de sesión")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
